// Route guard for the AiLys partner portal.
//
// Redirects to /login if no session cookie is present. Cookie presence is
// a coarse check; the authoritative auth happens server-side at every
// edge function (verifyWorkOSJwt). UI gating per ADR-0008 is cosmetic.
//
// Phase 6.1 Chunk d.

import { ReactNode, useEffect, useState } from 'react';

// Signal cookie is the non-HttpOnly companion of the session cookie.
// It contains minimal claims for SPA routing (role, org, exp). NOT
// authoritative; server-side auth via HttpOnly session cookie is the
// real gatekeeper. Checking the signal cookie is purely cosmetic.
const SIGNAL_COOKIE = 'ailys_partner_signal';

function hasSignalCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some((c) => c.trim().startsWith(`${SIGNAL_COOKIE}=`));
}

interface AuthGateProps {
  children: ReactNode;
  fallbackPath?: string;
}

export function AuthGate({ children, fallbackPath = '/login' }: AuthGateProps) {
  const [checked, setChecked] = useState(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (hasSignalCookie()) {
      setAuthed(true);
    } else {
      const next = window.location.pathname + window.location.search;
      const safe = next === '/' ? '' : `?next=${encodeURIComponent(next)}`;
      window.location.replace(`${fallbackPath}${safe}`);
    }
    setChecked(true);
  }, [fallbackPath]);

  if (!checked) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0e1a',
          color: '#94a3b8',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            border: '2px solid #1e293b',
            borderTopColor: '#67e8f9',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!authed) return null;

  return <>{children}</>;
}
